import type { InputMode, ReviewGroup, ReviewPart, TestModule, TestVariant } from './tests';

/** A question group as the candidate receives it — the answer key is absent. */
export type SittingGroup = Omit<ReviewGroup, 'questions'> & {
  questions: {
    id: string;
    number: number;
    /** Both ends of a prompt worth several marks; null on an ordinary one. */
    spanFrom: number | null;
    spanTo: number | null;
    promptHtml: string | null;
    options: { letter: string; text: string }[] | null;
    /** Where the gap sits on the artwork, when the paper places it. */
    at?: { x: number; y: number } | null;
  }[];
};

export type SittingPart = Omit<ReviewPart, 'groups'> & { groups: SittingGroup[] };

export interface Sitting {
  session: {
    id: string;
    state: 'IN_PROGRESS' | 'SUBMITTED' | 'EXPIRED';
    startedAt: string;
    deadlineAt: string;
    msRemaining: number;
    audioOffsetMs: number | null;
    submittedAt: string | null;
    autoSubmitted: boolean;
    /** When a Listening recording stopped, if it has; the tail runs from here. */
    recordingEndedAt?: string | null;
    serverNow: string;
  };
  candidate: { fullName: string; candidateNumber: string | null };
  test: {
    id: string;
    title: string;
    module: TestModule;
    variant: TestVariant;
    durationMs: number | null;
    /** In order. One entry for a continuous recording, one per part otherwise. */
    audio: { url: string; filename: string; durationMs: number | null }[];
  };
  parts: SittingPart[];
  answers: { questionNumber: number; value: string; flaggedForReview: boolean }[];
}

export interface AssignmentRow {
  id: string;
  status: 'OPEN' | 'STARTED' | 'COMPLETED' | 'CANCELLED';
  opensAt: string | null;
  closesAt: string | null;
  window: 'BEFORE' | 'OPEN' | 'AFTER';
  /**
   * The module that has to be finished before this one opens, if any.
   *
   * The papers are sat in the order the real test runs them — Listening,
   * Reading, Writing — and the server refuses a sitting out of turn whatever
   * this says. It is here so the screen can say why rather than let a
   * candidate press a button that will be turned down.
   */
  blockedBy?: TestModule | null;
  test: { id: string; title: string; module: TestModule; variant: TestVariant; durationMs: number | null };
  session: {
    id: string;
    state: 'IN_PROGRESS' | 'SUBMITTED' | 'EXPIRED';
    submittedAt: string | null;
    serverDeadlineAt: string;
  } | null;
}

export type { InputMode };

/** Every question number in the paper, in order. */
export function questionNumbers(parts: SittingPart[]): number[] {
  return parts.flatMap((p) => p.groups.flatMap((g) => g.questions.map((q) => q.number)));
}

/** Which part a question sits in, for the navigator. */
export function partOfQuestion(parts: SittingPart[], number: number): number {
  for (const part of parts) {
    for (const group of part.groups) {
      if (group.questions.some((q) => q.number === number)) return part.index;
    }
  }
  return parts[0]?.index ?? 1;
}

/** mm:ss, as the real exam clock shows it. */
export function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * What the bar's clock reads: minutes and seconds, all the way down.
 *
 * This showed whole minutes for the body of the paper and seconds only in the
 * last one, on the reasoning that an hour of digits changing every second is a
 * thing to watch rather than a paper to read. A candidate sitting it wanted
 * the seconds throughout, which is also what the real screen gives them, and
 * it is their exam.
 *
 * Never rounded up. A clock in an exam may only ever err against itself, so
 * the seconds are taken from the time actually left rather than from the
 * nearest second — 29:00 means at least twenty-nine minutes, never a moment
 * more than the clock says.
 */
export function remaining(ms: number): string {
  return clock(Math.max(0, ms));
}

/**
 * The same time in words: "1 minute, 58 seconds remaining".
 *
 * The bar's clock is digits, because it is read at a glance a hundred times.
 * This is read once, in the box that appears when the recording stops, and
 * there it is spelled out — which is how the real screen says it, and it is
 * unmistakable in a way that "1:58" beside a dozen other numbers is not.
 */
export function inWords(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  const say = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;
  const parts = minutes > 0 ? [say(minutes, 'minute'), say(seconds, 'second')] : [say(seconds, 'second')];
  return `${parts.join(', ')} remaining`;
}
